/**
 * The code form inside the "check your email" panel, shared by /login/ and
 * /register/ (#54).
 *
 * The resend is identical on both pages: `signInWithOtp` with
 * `shouldCreateUser: false`. On /register/ that is safe because the panel
 * only ever appears after `/api/register` has already answered 200, so the
 * account already exists by the time a resend can be clicked -- there is
 * nothing left for `shouldCreateUser: false` to refuse. Everything else is
 * identical by requirement too, including the error wording: both panels
 * appear whether or not the address has an account, so a message that varied
 * would answer through the back door the question the panels refuse to
 * answer at the front.
 */
import { supabase } from "./supabase";
import {
  runOtpCodeSignIn,
  resendSecondsLeft,
  formatCountdown,
  OTP_CODE_LENGTH,
} from "./otp-code-lib";
import { postCompleteRegistration } from "./complete-registration-client";
import { showFormError, clearFormError } from "./form-message-lib";
import { isButtonBusy, setButtonBusy } from "./button-busy";

export interface OtpFormDom {
  form: HTMLFormElement;
  input: HTMLInputElement;
  submit: HTMLButtonElement;
  resend: HTMLButtonElement;
  status: HTMLParagraphElement;
}

export function getOtpFormDom(): OtpFormDom {
  return {
    form: document.getElementById("codeForm") as HTMLFormElement,
    input: document.getElementById("codeInput") as HTMLInputElement,
    submit: document.getElementById("codeSubmit") as HTMLButtonElement,
    resend: document.getElementById("resendButton") as HTMLButtonElement,
    status: document.getElementById("codeStatus") as HTMLParagraphElement,
  };
}

/** Clears both places a previous attempt could have left a message. */
function clearFormMessages(dom: OtpFormDom): void {
  clearFormError(dom.form);
  dom.status.textContent = "";
}

/**
 * `sentAt` is when the mail went out, not when the panel was drawn by some
 * other route: GoTrue's minimum interval between two mails to one address runs
 * from the send, so a countdown started on the click would offer a resend the
 * server still refuses with 429.
 */
function startCountdown(dom: OtpFormDom, sentAt: number): void {
  const tick = () => {
    const left = resendSecondsLeft(sentAt, Date.now());
    dom.resend.disabled = left > 0;
    dom.resend.textContent =
      left > 0 ? `Resend code in ${formatCountdown(left)}` : "Resend code";
    if (left > 0) window.setTimeout(tick, 1000);
  };
  tick();
}

async function submitCode(dom: OtpFormDom, email: string): Promise<void> {
  clearFormMessages(dom);
  setButtonBusy(dom.submit, true);

  const result = await runOtpCodeSignIn({
    email,
    token: dom.input.value,
    verifyOtp: async (args) => {
      const { data, error } = await supabase.auth.verifyOtp(args);
      return { session: data.session, error };
    },
    completeRegistration: postCompleteRegistration,
  });

  if (result.ok) {
    window.location.href = result.target;
    return;
  }

  // One wording for every failure: GoTrue answers wrong, expired, spent and
  // never-existed alike with the same 403. The detail goes to the console.
  showFormError(
    dom.form,
    "That code didn't work. Check it, or request a new one.",
    result.detail,
  );
  setButtonBusy(dom.submit, false);
  // A failed verify does not consume the token, so leave it there to correct.
  dom.input.focus();
  dom.input.select();
}

export function wireOtpCodeForm(opts: {
  dom: OtpFormDom;
  email: string;
  sentAt: number;
}): void {
  const { dom, email, sentAt } = opts;
  startCountdown(dom, sentAt);

  // `dom.submit.disabled` (set inside `submitCode`) only blocks the button --
  // it does nothing against a scripted `form.requestSubmit()`. Without this
  // guard, a failed verify refocuses+selects the input (by requirement, so the
  // code stays correctable), and pasting a fresh 6-digit code while the first
  // `verifyOtp` is still pending would start a second `submitCode` concurrently.
  // Plain closure state is enough: this form has one input in flight at a time.
  let inFlight = false;

  const startSubmit = () => {
    if (inFlight) return;
    inFlight = true;
    void submitCode(dom, email).finally(() => {
      inFlight = false;
    });
  };

  dom.input.addEventListener("input", () => {
    // Digits only, so a pasted "123 456" or "code: 123456" still works.
    dom.input.value = dom.input.value
      .replace(/\D/g, "")
      .slice(0, OTP_CODE_LENGTH);
    // A busy button stays disabled until its verify answers.
    if (!isButtonBusy(dom.submit))
      dom.submit.disabled = dom.input.value.length !== OTP_CODE_LENGTH;
    if (dom.input.value.length === OTP_CODE_LENGTH) dom.form.requestSubmit();
  });

  dom.form.addEventListener("submit", (event) => {
    event.preventDefault();
    startSubmit();
  });

  dom.resend.addEventListener("click", () => {
    // A resend while a verify is still pending would clear the input and
    // disable submit out from under it; the pending failure branch would then
    // re-enable submit over an empty field, so a click would report
    // `reason: "empty"` as "That code didn't work." Sharing the submit path's
    // guard keeps a resend from starting mid-verify.
    if (inFlight) return;
    void (async () => {
      clearFormMessages(dom);
      dom.resend.disabled = true;

      // Silent for the same reason the first send is: an address without an
      // account is an expected outcome of this form, not a fault, and the
      // outcome must not distinguish the two -- so a resend failure is
      // reported the same way for every address, like the first send.
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
      });
      if (error) console.info("code not resent", error);

      dom.status.textContent = "New code sent.";
      dom.input.value = "";
      dom.submit.disabled = true;
      startCountdown(dom, Date.now());
    })();
  });
}
